import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../Button/Button.js';
import { Input } from '../Input/Input.js';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu.js';
import './ThemeDropdown.css';

export const ThemeDropdown = ({
  isOpen = false,
  onClose,
  onCreateTheme,
  onRenameTheme,
  onDeleteTheme,
  currentTheme,
  buttonRef,
  // When false (e.g. Primitives collection) we hide theme-specific actions
  hasThemeOptions = true,
  // Triggered when user chooses "Rename collection"
  onRenameCollection,
  className = '',
  ...props
}) => {
  const [mode, setMode] = useState('menu'); // 'menu', 'create', 'rename'
  const [inputValue, setInputValue] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Calculate vertical position from button when opening
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Start with a provisional left aligned to the button's right edge.
      // We'll refine it in a follow-up effect once the menu has measured itself.
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right
      });
    }
  }, [isOpen, buttonRef]);

  // After the menu is rendered, measure its width and align its right edge
  // with the trigger button's right edge so the dropdown doesn't drift.
  useEffect(() => {
    if (!isOpen || !buttonRef?.current || !dropdownRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuRect = dropdownRef.current.getBoundingClientRect();

    const left = Math.max(8, buttonRect.right - menuRect.width);

    setDropdownPosition((prev) => ({
      ...prev,
      left
    }));
  }, [isOpen, buttonRef]);

  // Handle click outside, scroll and resize
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          buttonRef?.current &&
          !buttonRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const handleDismiss = () => {
      handleClose();
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Defer scroll/resize listeners by one frame to avoid catching
    // layout-triggered scroll events during the initial render
    const rafId = requestAnimationFrame(() => {
      window.addEventListener('scroll', handleDismiss, true);
      window.addEventListener('resize', handleDismiss);
    });

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [isOpen, buttonRef]);

  // Focus input when switching to input modes
  useEffect(() => {
    if ((mode === 'create' || mode === 'rename') && inputRef.current) {
      inputRef.current.focus();
      if (mode === 'rename') {
        setInputValue(currentTheme?.name || '');
      }
    }
  }, [mode, currentTheme]);

  const handleClose = () => {
    setMode('menu');
    setInputValue('');
    onClose?.();
  };

  const handleCreate = () => {
    if (inputValue.trim()) {
      onCreateTheme?.(inputValue.trim());
      handleClose();
    }
  };

  const handleRename = () => {
    if (inputValue.trim() && inputValue.trim() !== currentTheme?.name) {
      onRenameTheme?.(currentTheme?.id, inputValue.trim());
      handleClose();
    }
  };

  const handleDelete = () => {
    onDeleteTheme?.(currentTheme?.id);
    handleClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (mode === 'create') {
        handleCreate();
      } else if (mode === 'rename') {
        handleRename();
      }
    } else if (e.key === 'Escape') {
      if (mode === 'create' || mode === 'rename') {
        setMode('menu');
        setInputValue('');
      } else {
        handleClose();
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <DropdownMenu
      menuRef={dropdownRef}
      fixed
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`
      }}
      className={`theme-dropdown ${className}`}
      {...props}
    >
      {mode === 'menu' && (
        <>
          <DropdownMenu.Item
            onClick={() => {
              onRenameCollection?.();
              handleClose();
            }}
          >
            Rename collection
          </DropdownMenu.Item>

          {hasThemeOptions && (
            <>
              <DropdownMenu.Divider />
              <DropdownMenu.Item onClick={() => setMode('create')}>
                Create theme
              </DropdownMenu.Item>
              <DropdownMenu.Item onClick={() => setMode('rename')}>
                Rename theme
              </DropdownMenu.Item>
              <DropdownMenu.Item danger onClick={handleDelete}>
                Delete theme
              </DropdownMenu.Item>
            </>
          )}
        </>
      )}

      {mode === 'create' && (
        <div className="theme-dropdown__input-mode">
          <div className="theme-dropdown__header">Create theme</div>
          <Input
            ref={inputRef}
            type="text"
            style="outline"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Theme name"
            className="theme-dropdown__input"
          />
          <div className="theme-dropdown__actions">
            <Button
              type="ghost"
              size="small"
              onClick={() => setMode('menu')}
            >
              Cancel
            </Button>
            <Button
              type="solid"
              size="small"
              onClick={handleCreate}
              disabled={!inputValue.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {mode === 'rename' && (
        <div className="theme-dropdown__input-mode">
          <div className="theme-dropdown__header">Rename theme</div>
          <Input
            ref={inputRef}
            type="text"
            style="outline"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Theme name"
            className="theme-dropdown__input"
          />
          <div className="theme-dropdown__actions">
            <Button
              type="ghost"
              size="small"
              onClick={() => setMode('menu')}
            >
              Cancel
            </Button>
            <Button
              type="solid"
              size="small"
              onClick={handleRename}
              disabled={!inputValue.trim() || inputValue.trim() === currentTheme?.name}
            >
              Rename
            </Button>
          </div>
        </div>
      )}
    </DropdownMenu>,
    document.body
  );
};
